// Mirrors server/utils/flexibleAmount.js GATEWAY_MIN — keep them in step.
export const GATEWAY_MIN = { USD: 1, GBP: 1, NGN: 100 };

export function fmtMoney(amount, currency) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(2)}`;
  }
}

// One-line summary under the flexible fields, or null while the total isn't a number yet.
export function flexiblePreviewText({ amount, minPayment, currency }) {
  const total = parseFloat(amount);
  if (!total || total <= 0) return null;
  const min = Math.min(total, Math.max(parseFloat(minPayment) || 0, GATEWAY_MIN[currency] ?? 1));
  return `Customer pays any amount from ${fmtMoney(min, currency)} until ${fmtMoney(
    total,
    currency
  )} is cleared.`;
}

// "£700.00 of £1,200.00" — what has come in (net of refunds) against the total.
export function flexibleAmountLabel(link) {
  const net = Math.round(((link.amountPaid || 0) - (link.refundedAmount || 0)) * 100) / 100;
  return `${fmtMoney(net, link.currency)} of ${fmtMoney(link.amount, link.currency)}`;
}

// Shareable-link pricing modes, in the order the create form shows them.
export const PRICING_MODES = {
  parts: {
    label: "Pay in parts",
    icon: "pie_chart",
    amountLabel: "Total",
    help: "Payers pay what they can now and get their own link to pay the rest later.",
  },
  fixed: {
    label: "Fixed price",
    icon: "sell",
    amountLabel: "Price",
    help: "Everyone pays the same amount.",
  },
  any: {
    label: "Any amount",
    icon: "edit",
    amountLabel: null,
    help: "Each payer types what they want to pay, as a one-off payment.",
  },
};

export function openLinkPriceLabel(link) {
  if (link.pricingMode === "fixed") return fmtMoney(link.amount, link.currency);
  if (link.pricingMode === "parts") return `${fmtMoney(link.amount, link.currency)} total`;
  const min = Math.max(link.minPayment || 0, GATEWAY_MIN[link.currency] ?? 1);
  return `From ${fmtMoney(min, link.currency)}`;
}

// One-line summary under the create form, or null until a price/total is entered.
export function openLinkPreviewText({ pricingMode, amount, minPayment, currency }) {
  const min = Math.max(parseFloat(minPayment) || 0, GATEWAY_MIN[currency] ?? 1);
  if (pricingMode === "any") return `Each payer chooses an amount from ${fmtMoney(min, currency)}.`;
  const total = parseFloat(amount);
  if (!total || total <= 0) return null;
  if (pricingMode === "fixed") return `Everyone pays ${fmtMoney(total, currency)}.`;
  return `Payers pay from ${fmtMoney(
    Math.min(min, total),
    currency
  )} now and get their own link to clear the rest of ${fmtMoney(total, currency)}.`;
}
